import { Atom, Stethoscope, GraduationCap, Laptop, Landmark, TrendingUp, Award } from 'lucide-react'
import { Link } from '@inertiajs/react'
import { Button } from '@/components/ui/button'

export interface ExamSummary {
  id: number
  name: string
  code: string
  year?: number | null
  tests_count?: number
}

interface ExamCategoriesSectionProps {
  exams?: ExamSummary[]
  user?: any
}

interface CategoryCard {
  id: string
  dbId?: number
  title: string
  description: string
  icon: any
  isGateBadge?: boolean
  isAvailable: boolean
  testsCount?: number
  href?: string
}

function getExamIcon(code?: string, name?: string) {
  const text = `${code || ''} ${name || ''}`.toLowerCase()
  if (text.includes('nism') || text.includes('mutual fund') || text.includes('mfd')) {
    return TrendingUp
  }
  if (text.includes('bank') || text.includes('sbi') || text.includes('ibps') || text.includes('po') || text.includes('clerk')) {
    return Landmark
  }
  if (text.includes('neet') || text.includes('medical')) {
    return Stethoscope
  }
  if (text.includes('jee') || text.includes('engineering') || text.includes('iit')) {
    return Atom
  }
  if (text.includes('upsc') || text.includes('ias') || text.includes('civil')) {
    return GraduationCap
  }
  if (text.includes('cat') || text.includes('mba') || text.includes('management')) {
    return Laptop
  }
  return Award
}

function getExamDescription(code?: string, name?: string, testsCount = 0) {
  const text = `${code || ''} ${name || ''}`.toLowerCase()
  if (text.includes('nism') || text.includes('mutual fund') || text.includes('mfd')) {
    return 'Official certification mock tests with syllabus weightage and structured practice questions.'
  }
  if (text.includes('sbi') || text.includes('bank') || text.includes('clerk') || text.includes('po')) {
    return 'Prepare for Bank PO, SBI, and IBPS exams with sectional timers and percentile ranking.'
  }
  if (text.includes('neet')) {
    return 'Practice curated mock tests, analyze performance, and learn detailed solutions.'
  }
  if (text.includes('jee')) {
    return 'Engineering entrance prep with Physics, Chemistry, and Mathematics mock tests.'
  }
  if (text.includes('upsc')) {
    return 'Practice comprehensive mock tests for UPSC Prelims and civil services.'
  }
  if (text.includes('cat')) {
    return 'Access full-length mock tests in CAT with sectional time limits, speed drills, and analytics.'
  }
  return `Comprehensive practice mock tests covering latest syllabus patterns and questions.`
}

const DUMMY_EXAMS: CategoryCard[] = [
  {
    id: 'dummy-jee',
    title: 'JEE Mains',
    description: 'Enable with competitive exams for engineering and technical students.',
    icon: Atom,
    isGateBadge: false,
    isAvailable: false,
  },
  {
    id: 'dummy-neet',
    title: 'NEET UG',
    description: 'Practice high-yield questions, analyze performance, and master medical entrance concepts.',
    icon: Stethoscope,
    isGateBadge: false,
    isAvailable: false,
  },
  {
    id: 'dummy-upsc',
    title: 'UPSC Prelims',
    description: 'Practice comprehensive mock tests for UPSC Prelims and civil services.',
    icon: GraduationCap,
    isGateBadge: false,
    isAvailable: false,
  },
  {
    id: 'dummy-cat',
    title: 'CAT',
    description: 'Access full-length mock tests in CAT with sectional time limits, speed drills, and analytics.',
    icon: Laptop,
    isGateBadge: false,
    isAvailable: false,
  },
  {
    id: 'dummy-gate',
    title: 'GATE 2027',
    description: 'Official IIT Madras CBT pattern with MCQ, MSQ, and NAT questions with 4-method solutions.',
    icon: null,
    isGateBadge: true,
    isAvailable: false,
    href: '/exams/gate-preparation',
  },
]

export function ExamCategoriesSection({ exams = [], user }: ExamCategoriesSectionProps) {
  // DB exams are sorted in HomeController with NISM in the first position
  const dbCategories: CategoryCard[] = exams.map((exam) => {
    const isAvailable = (exam.tests_count || 0) > 0
    const Icon = getExamIcon(exam.code, exam.name)
    const desc = getExamDescription(exam.code, exam.name, exam.tests_count)

    return {
      id: `db-exam-${exam.id}`,
      dbId: exam.id,
      title: exam.name,
      description: desc,
      icon: Icon,
      isGateBadge: false,
      isAvailable: isAvailable,
      testsCount: exam.tests_count || 0,
    }
  })

  // Filter dummy exams that aren't already represented in DB
  const existingNames = new Set(
    exams.map(e => `${e.code} ${e.name}`.toLowerCase())
  )

  const filteredDummy = DUMMY_EXAMS.filter(dummy => {
    const dummyKey = dummy.title.toLowerCase().split(' ')[0]
    return !Array.from(existingNames).some(name => name.includes(dummyKey))
  })

  // Live DB exams first (with NISM in place 1), followed by dummy coming soon exams
  const displayCategories = [...dbCategories, ...filteredDummy]

  return (
    <section id="exams" className="w-full py-16 sm:py-24 bg-[#f8fafc] border-y border-slate-100">
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Heading */}
        <div className="text-center max-w-3xl mx-auto mb-14 sm:mb-16">
          <h2 className="!font-sans text-3xl sm:text-4xl lg:text-[42px] font-black text-[#131b2e] tracking-tight">
            Explore Exam Categories
          </h2>
        </div>

        {/* 3x2 Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {displayCategories.map((cat) => {
            const Icon = cat.icon
            return (
              <div
                key={cat.id}
                className={`bg-white border border-slate-200 rounded-2xl p-6 sm:p-7 flex flex-col justify-between min-h-[220px] shadow-xs transition-all duration-200 ${
                  cat.isAvailable
                    ? 'hover:border-[#c8933b]/60 hover:shadow-md'
                    : 'hover:border-slate-300'
                }`}
              >
                <div className="space-y-3.5">
                  <div className="flex items-center justify-between">
                    {/* Icon Box */}
                    <div
                      className={`w-12 h-12 rounded-xl flex items-center justify-center transition-colors ${
                        cat.isAvailable
                          ? 'bg-amber-50 border border-amber-200 text-[#c8933b] shadow-2xs'
                          : 'bg-slate-100 border border-slate-200 text-slate-400'
                      }`}
                    >
                      {cat.isGateBadge ? (
                        <span className="font-black text-xs tracking-wider text-[#131b2e]">GATE</span>
                      ) : Icon ? (
                        <Icon className="w-6 h-6 stroke-[2.2]" />
                      ) : null}
                    </div>

                    {!cat.isAvailable ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-500 border border-slate-200">
                        Coming Soon
                      </span>
                    ) : (
                      cat.testsCount != null && cat.testsCount > 0 && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-amber-50 text-amber-800 border border-amber-200 shadow-2xs">
                          {cat.testsCount} Mock Test{cat.testsCount > 1 ? 's' : ''}
                        </span>
                      )
                    )}
                  </div>

                  {/* Title & Description */}
                  <div className="space-y-1.5">
                    <h3 className="!font-sans text-xl font-bold text-[#131b2e] line-clamp-1" title={cat.title}>
                      {cat.title}
                    </h3>
                    <p className="!font-sans text-sm text-slate-600 leading-relaxed line-clamp-2">
                      {cat.description}
                    </p>
                  </div>
                </div>

                {/* Action Button */}
                {cat.isAvailable ? (
                  <div className="pt-4">
                    <Button
                      asChild
                      variant="outline"
                      className="border border-slate-200 text-[#131b2e] hover:bg-[#c8933b] hover:text-white hover:border-[#c8933b] font-bold rounded-lg px-5 h-9 text-xs transition-colors cursor-pointer"
                    >
                      {user && cat.dbId ? (
                        <Link href={`/student/exams/${cat.dbId}`}>
                          Start Test
                        </Link>
                      ) : (
                        <a href="/users/sign_up">
                          Start Test
                        </a>
                      )}
                    </Button>
                  </div>
                ) : cat.href ? (
                  <div className="pt-4">
                    <Button
                      asChild
                      variant="outline"
                      className="border border-slate-200 text-[#131b2e] hover:bg-slate-100 font-bold rounded-lg px-4 h-9 text-xs transition-colors cursor-pointer"
                    >
                      <Link href={cat.href}>
                        Exam Pattern & Guide
                      </Link>
                    </Button>
                  </div>
                ) : null}
              </div>
            )
          })}
        </div>

      </div>
    </section>
  )
}
